import React from 'react';
import { Box, Typography } from '@mui/material';

// SVG semicircle gauge styled like the Crypto Fear & Greed Index reference image.
// Renders a continuous gradient arc (red → orange → yellow → green) with a small
// triangular pointer indicating `value` (0–100) and centre text + label.
//
// Props
//   value      number  — score in [0,100]
//   title      string  — header above the gauge
//   levelLabel string  — "Bình thường" / "Cảnh báo" / "Báo động" (renders below value)
//   levelColor string  — colour used for level label + needle stroke
//   yesterday  number? — secondary score shown bottom-left ("Hôm qua")
//   lastWeek   number? — secondary score shown bottom-right ("Tuần trước")
//   reverse    boolean — when true the gradient is mirrored (high = good, e.g. Discipline)
//
// Layout: viewBox 200x140; the arc spans 180° from (10,110) → (190,110) with r=90.
export default function HalfCircleGauge({
  value,
  title,
  levelLabel,
  levelColor = '#94a3b8',
  yesterday,
  lastWeek,
  reverse = false,
  subtitle,
}) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  // 0 → angle 180° (left). 100 → 0° (right).
  const angleDeg = 180 - (v / 100) * 180;
  const rad = (angleDeg * Math.PI) / 180;
  const cx = 100;
  const cy = 110;
  const r  = 90;
  // Needle tip on the inner arc (slightly inside the stroke)
  const tipR = r - 4;
  const tipX = cx + tipR * Math.cos(rad);
  const tipY = cy - tipR * Math.sin(rad);

  // Use a unique gradient id per component so two gauges on the same page
  // don't collide. React.useId requires unconditional call (rules-of-hooks).
  const reactId = React.useId();
  const gradId = `gauge-${reactId.replace(/:/g, '')}`;

  // Reversed gradient: green on left, red on right (high score = good).
  const stops = reverse
    ? [
        { o: '0%',   c: '#ef4444' },
        { o: '40%',  c: '#f97316' },
        { o: '60%',  c: '#fbbf24' },
        { o: '100%', c: '#22c55e' },
      ]
    : [
        { o: '0%',   c: '#22c55e' },
        { o: '40%',  c: '#fbbf24' },
        { o: '60%',  c: '#f97316' },
        { o: '100%', c: '#ef4444' },
      ];

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {title && (
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary', mb: 0.5 }}>
          {title}
        </Typography>
      )}
      <svg viewBox="0 0 200 140" width="100%" style={{ maxWidth: 280 }}>
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
            {stops.map(s => <stop key={s.o} offset={s.o} stopColor={s.c} />)}
          </linearGradient>
        </defs>
        {/* Background track */}
        <path
          d="M 10 110 A 90 90 0 0 1 190 110"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="18"
          fill="none"
          strokeLinecap="round"
        />
        {/* Foreground gradient arc */}
        <path
          d="M 10 110 A 90 90 0 0 1 190 110"
          stroke={`url(#${gradId})`}
          strokeWidth="18"
          fill="none"
          strokeLinecap="round"
        />
        {/* Needle: small triangle pointing at the value */}
        <g transform={`rotate(${-(90 - angleDeg)} ${tipX} ${tipY})`}>
          <polygon
            points={`${tipX - 6},${tipY + 8} ${tipX + 6},${tipY + 8} ${tipX},${tipY - 4}`}
            fill="rgba(255,255,255,0.95)"
            stroke={levelColor}
            strokeWidth="1.2"
          />
        </g>
        {/* Centre value */}
        <text
          x="100" y="92"
          textAnchor="middle"
          fill="#fff"
          style={{ fontSize: 26, fontWeight: 700, fontFamily: 'inherit' }}
        >
          {Math.round(v)}
        </text>
        {/* Level label */}
        <text
          x="100" y="115"
          textAnchor="middle"
          fill={levelColor}
          style={{ fontSize: 11, fontWeight: 600, fontFamily: 'inherit' }}
        >
          {levelLabel || ''}
        </text>
      </svg>

      {(yesterday != null || lastWeek != null) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 280, mt: 0.5, px: 1 }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.1 }}>Hôm qua</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600 }}>
              {yesterday != null ? Math.round(yesterday) : '—'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1.1 }}>Tuần trước</Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 600 }}>
              {lastWeek != null ? Math.round(lastWeek) : '—'}
            </Typography>
          </Box>
        </Box>
      )}
      {subtitle && (
        <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 0.5, textAlign: 'center' }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

// Replicates the EA's AlertLvl() heuristic:
//   normal score (higher = worse): score < thr*0.6 → Bình thường, < thr → Cảnh báo, else Báo động
//   inverted (higher = better, e.g. Discipline): score > thr*1.67 → Bình thường, > thr → Cảnh báo, else Báo động
export function alertLevel(score, threshold, inverted = false) {
  if (score == null || threshold == null) return { label: '—', color: '#94a3b8' };
  if (inverted) {
    if (score > threshold * 1.67) return { label: 'Bình thường', color: '#4ade80' };
    if (score > threshold)        return { label: 'Cảnh báo',    color: '#fbbf24' };
    return { label: 'Báo động', color: '#f87171' };
  }
  if (score < threshold * 0.6) return { label: 'Bình thường', color: '#4ade80' };
  if (score < threshold)       return { label: 'Cảnh báo',    color: '#fbbf24' };
  return { label: 'Báo động', color: '#f87171' };
}
